import type { ElkNode } from "elkjs";
import {
  type GraphGeometry,
  graphGeometryBounds,
  type LayoutBox,
  type LayoutConnection,
  layoutGeneralGraph,
  pointsToPath,
} from "./general-layout";
import type { RuleGraphPosition } from "./layout";

export interface LayoutGroup {
  id: string;
  headerId?: string;
  nodeIds: string[];
}
interface PlacedGroup {
  id: string;
  width: number;
  height: number;
  positions: Map<string, RuleGraphPosition>;
  paths: Map<string, string>;
  entrances: Map<string, string>;
  exits: Map<string, string>;
}
function pathPoints(path: string): RuleGraphPosition[] {
  return [...path.matchAll(/[ML] ([\d.e+-]+),([\d.e+-]+)/g)].map((match) => ({
    x: Number(match[1]),
    y: Number(match[2]),
  }));
}

function translated(path: string, x: number, y: number): string {
  return pointsToPath(
    pathPoints(path).map((point) => ({ x: point.x + x, y: point.y + y }))
  );
}

/** ELK places steps, headers, groups, and routes. Synthetic nodes only join group ports. */
export async function layoutGroupedGraph(
  boxes: LayoutBox[],
  connections: LayoutConnection[],
  groups: LayoutGroup[],
  execute: (graph: ElkNode) => Promise<ElkNode>,
  sharedTailNodeIds: string[] = []
): Promise<GraphGeometry> {
  if (groups.length === 0)
    return layoutGeneralGraph(boxes, connections, execute);
  const byId = new Map(boxes.map((box) => [box.id, box]));
  const owner = new Map<string, string>();
  const definitions: (LayoutGroup & { standalone?: boolean })[] = groups
    .map((group, index) => ({
      ...group,
      id: `group-${index}`,
      nodeIds: group.nodeIds.filter((id) => byId.has(id)),
    }))
    .filter((group) => group.nodeIds.length > 0);
  for (const group of definitions)
    for (const id of group.nodeIds) owner.set(id, group.id);
  let index = groups.length;
  for (const box of [...boxes].sort((a, b) => a.id.localeCompare(b.id))) {
    if (!owner.has(box.id)) {
      const id = `group-${index++}`;
      owner.set(box.id, id);
      definitions.push({ id, nodeIds: [box.id], standalone: true });
    }
  }
  const cross = connections.filter(
    (edge) => owner.get(edge.source) !== owner.get(edge.target)
  );
  const placed: PlacedGroup[] = [];
  for (const group of definitions) {
    if (group.standalone) {
      const box = byId.get(group.nodeIds[0]!)!;
      placed.push({
        id: group.id,
        width: box.width,
        height: box.height,
        positions: new Map([[box.id, { x: 0, y: 0 }]]),
        paths: new Map(),
        entrances: new Map(),
        exits: new Map(),
      });
      continue;
    }
    const header = group.headerId ? byId.get(group.headerId) : undefined;
    const pipeline = group.nodeIds.filter((id) => id !== header?.id);
    const members = new Set(pipeline);
    const inside = connections.filter(
      (edge) => members.has(edge.source) && members.has(edge.target)
    );
    const localBoxes = pipeline.map((id) => byId.get(id)!);
    const localEdges = [...inside];
    const entrances = new Map<string, string>(),
      exits = new Map<string, string>();
    // Boundary proxies let ELK route between interior steps and the outside of
    // the group. A straight extension from an interior card can cross its peers.
    for (const [i, edge] of cross.entries()) {
      for (const end of ["source", "target"] as const) {
        if (!members.has(edge[end])) continue;
        let proxy = `@layout-${i}-${end}`;
        while (byId.has(proxy)) proxy = `_${proxy}`;
        localBoxes.push({
          id: proxy,
          width: 1,
          height: 1,
          layer: end === "source" ? "LAST" : "FIRST",
        });
        const partId = `@part-${i}-${end}`;
        if (end === "source") {
          exits.set(edge.id, partId);
          localEdges.push({
            ...edge,
            id: partId,
            target: proxy,
            targetHandle: "flow-target",
          });
        } else {
          entrances.set(edge.id, partId);
          localEdges.push({
            ...edge,
            id: partId,
            source: proxy,
            sourceHandle: "flow-source",
          });
        }
      }
    }
    const geometry = await layoutGeneralGraph(
      localBoxes,
      localEdges,
      execute,
      header ? "row" : "canvas"
    );
    let width = 0,
      height = 0;
    for (const box of localBoxes) {
      const point = geometry.positionsByNodeId.get(box.id)!;
      width = Math.max(width, point.x + box.width);
      height = Math.max(height, point.y + box.height);
    }
    // Fork routes can extend above or below their cards. Reserve that space
    // inside the group so the next header cannot land on a bypass connection.
    const originX = Math.min(0, geometry.bounds.x);
    const originY = Math.min(0, geometry.bounds.y);
    width =
      Math.max(width, geometry.bounds.x + geometry.bounds.width) - originX;
    height =
      Math.max(height, geometry.bounds.y + geometry.bounds.height) - originY;
    let dx = -originX,
      dy = -originY;
    const positions = new Map<string, RuleGraphPosition>();
    if (header) {
      const frame = await execute({
        id: "frame",
        layoutOptions: {
          "elk.algorithm": "layered",
          "elk.direction": "DOWN",
          "elk.padding": "[top=0,left=0,bottom=0,right=0]",
          "elk.layered.spacing.nodeNodeBetweenLayers": "12",
          "elk.layered.nodePlacement.bk.fixedAlignment": "LEFTUP",
        },
        children: [
          {
            ...header,
            id: "header",
            layoutOptions: { "elk.portConstraints": "FIXED_POS" },
            ports: [
              {
                id: "header-out",
                x: 0,
                y: header.height,
                width: 0,
                height: 0,
                layoutOptions: { "elk.port.side": "SOUTH" },
              },
            ],
          },
          {
            id: "pipeline",
            width: Math.max(width, 1),
            height: Math.max(height, 1),
            layoutOptions: { "elk.portConstraints": "FIXED_POS" },
            ports: [
              {
                id: "pipeline-in",
                x: 0,
                y: 0,
                width: 0,
                height: 0,
                layoutOptions: { "elk.port.side": "NORTH" },
              },
            ],
          },
        ],
        edges: [
          {
            id: "placement",
            sources: ["header-out"],
            targets: ["pipeline-in"],
          },
        ],
      });
      const title = frame.children!.find((node) => node.id === "header")!;
      const row = frame.children!.find((node) => node.id === "pipeline")!;
      positions.set(header.id, { x: title.x!, y: title.y! });
      dx = row.x! - originX;
      dy = row.y! - originY;
      width = frame.width!;
      height = frame.height!;
    }
    for (const id of pipeline) {
      const point = geometry.positionsByNodeId.get(id)!;
      positions.set(id, { x: point.x + dx, y: point.y + dy });
    }
    const paths = new Map(
      inside.map((edge) => [
        edge.id,
        translated(geometry.pathsByEdgeId.get(edge.id)!, dx, dy),
      ])
    );
    const translateParts = (parts: Map<string, string>) =>
      new Map(
        [...parts].map(([id, part]) => [
          id,
          translated(geometry.pathsByEdgeId.get(part)!, dx, dy),
        ])
      );
    placed.push({
      id: group.id,
      width,
      height,
      positions,
      paths,
      entrances: translateParts(entrances),
      exits: translateParts(exits),
    });
  }
  const groupById = new Map(placed.map((group) => [group.id, group]));
  const ports = new Map<string, NonNullable<ElkNode["ports"]>>();
  const localEnds = new Map<
    string,
    { source: RuleGraphPosition; target: RuleGraphPosition }
  >();
  const edges = cross.map((edge, i) => {
    const endpoints = { source: { x: 0, y: 0 }, target: { x: 0, y: 0 } };
    for (const end of ["source", "target"] as const) {
      const box = byId.get(edge[end])!,
        group = groupById.get(owner.get(edge[end])!)!;
      const point = group.positions.get(box.id)!;
      const part = (end === "source" ? group.exits : group.entrances).get(
        edge.id
      );
      const fallback =
        edge[end === "source" ? "sourceHandle" : "targetHandle"]?.startsWith(
          "fallback"
        ) ?? false;
      const isHeader =
        box.header === true ||
        definitions.some((candidate) => candidate.headerId === box.id);
      const points = part ? pathPoints(part) : [];
      let endpoint: RuleGraphPosition;
      if (part) {
        endpoint = end === "source" ? points.at(-1)! : points[0]!;
      } else {
        let offsetX = 0;
        let offsetY = box.height / 2;
        if (fallback) {
          offsetX = isHeader ? 5 : box.width / 2;
          offsetY = end === "source" ? box.height : 0;
        } else if (end === "source") {
          offsetX = box.width;
        }
        endpoint = { x: point.x + offsetX, y: point.y + offsetY };
      }
      endpoints[end] = endpoint;
      let side = end === "source" ? "EAST" : "WEST";
      if (!part && fallback) {
        side = end === "source" ? "SOUTH" : "NORTH";
      }
      const list = ports.get(group.id) ?? [];
      list.push({
        id: `p-${i}-${end}`,
        ...endpoint,
        width: 0,
        height: 0,
        layoutOptions: { "elk.port.side": side },
      });
      ports.set(group.id, list);
    }
    localEnds.set(edge.id, endpoints);
    return {
      id: edge.id,
      sources: [`p-${i}-source`],
      targets: [`p-${i}-target`],
    };
  });
  const ordered = definitions.filter((group) => group.headerId !== undefined);
  for (const group of ordered) {
    const box = groupById.get(group.id)!;
    const list = ports.get(group.id) ?? [];
    list.push(
      {
        id: `${group.id}-order-in`,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        layoutOptions: { "elk.port.side": "NORTH" },
      },
      {
        id: `${group.id}-order-out`,
        x: 0,
        y: box.height,
        width: 0,
        height: 0,
        layoutOptions: { "elk.port.side": "SOUTH" },
      }
    );
    ports.set(group.id, list);
  }
  const ordering = ordered.slice(1).map((group, i) => ({
    id: `ordering-${i}`,
    sources: [`${ordered[i]!.id}-order-out`],
    targets: [`${group.id}-order-in`],
  }));
  const root = await execute({
    id: "groups",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "DOWN",
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.spacing.nodeNode": "48",
      "elk.layered.spacing.nodeNodeBetweenLayers":
        ordered.length > 0 ? "12" : "36",
      "elk.randomSeed": "1",
      "elk.padding": "[top=32,left=32,bottom=32,right=32]",
    },
    children: placed.map((group) => ({
      id: group.id,
      width: group.width,
      height: group.height,
      ports: ports.get(group.id) ?? [],
      layoutOptions: { "elk.portConstraints": "FIXED_POS" },
    })),
    edges: [...edges, ...ordering],
  });
  const rootById = new Map(root.children?.map((node) => [node.id, node]));
  const positionsByNodeId = new Map<string, RuleGraphPosition>(),
    pathsByEdgeId = new Map<string, string>();
  for (const box of root.children ?? []) {
    const group = groupById.get(box.id)!;
    for (const [id, point] of group.positions)
      positionsByNodeId.set(id, { x: point.x + box.x!, y: point.y + box.y! });
    for (const [id, path] of group.paths)
      pathsByEdgeId.set(id, translated(path, box.x!, box.y!));
  }
  for (const edge of cross) {
    const route = root.edges!.find((candidate) => candidate.id === edge.id)!;
    if (!route.sections?.length) throw new Error("Unrouted group connection");
    const points: RuleGraphPosition[] = [];
    const partPoints = (end: "source" | "target") => {
      const group = groupById.get(owner.get(edge[end])!)!,
        position = rootById.get(group.id)!;
      const path = (end === "source" ? group.exits : group.entrances).get(
        edge.id
      );
      const local = path ? pathPoints(path) : [localEnds.get(edge.id)![end]];
      return local.map((point) => ({
        x: point.x + position.x!,
        y: point.y + position.y!,
      }));
    };
    points.push(...partPoints("source"));
    for (const section of route.sections)
      points.push(
        section.startPoint,
        ...(section.bendPoints ?? []),
        section.endPoint
      );
    points.push(...partPoints("target"));
    pathsByEdgeId.set(edge.id, pointsToPath(points));
  }
  if (
    positionsByNodeId.size !== boxes.length ||
    pathsByEdgeId.size !== connections.length
  )
    throw new Error("Incomplete grouped layout");
  await centerSharedTail(
    boxes,
    connections,
    groups,
    sharedTailNodeIds,
    positionsByNodeId,
    pathsByEdgeId,
    execute
  );
  return {
    positionsByNodeId,
    pathsByEdgeId,
    bounds: graphGeometryBounds(boxes, positionsByNodeId, pathsByEdgeId),
  };
}

const SHARED_TAIL_GAP = 64;

/** Shared results belong beside the span of contributing rows, not after its bottom row. */
async function centerSharedTail(
  boxes: LayoutBox[],
  connections: LayoutConnection[],
  groups: LayoutGroup[],
  tailIds: string[],
  positions: Map<string, RuleGraphPosition>,
  paths: Map<string, string>,
  execute: (graph: ElkNode) => Promise<ElkNode>
): Promise<void> {
  const byId = new Map(boxes.map((box) => [box.id, box]));
  const tail = tailIds.filter((id) => byId.has(id));
  const first = tail[0];
  if (!first) return;
  const tailSet = new Set(tail);
  const incoming = connections.filter(
    (edge) => tailSet.has(edge.target) && !tailSet.has(edge.source)
  );
  if (
    !incoming.length ||
    incoming.some(
      (edge) => edge.target !== first || edge.sourceHandle !== "flow-source"
    )
  )
    return;
  const rowIds = new Set(
    groups
      .filter((group) =>
        incoming.some((edge) => group.nodeIds.includes(edge.source))
      )
      .flatMap((group) => group.nodeIds)
  );
  if (!rowIds.size) return;
  const centers = incoming.map(
    (edge) => positions.get(edge.source)!.y + byId.get(edge.source)!.height / 2
  );
  const centerY = (Math.min(...centers) + Math.max(...centers)) / 2;
  const right = Math.max(
    ...[...rowIds].map((id) => positions.get(id)!.x + byId.get(id)!.width)
  );
  const internal = connections.filter(
    (edge) => tailSet.has(edge.source) && tailSet.has(edge.target)
  );
  const geometry = await layoutGeneralGraph(
    tail.map((id) => byId.get(id)!),
    internal,
    execute,
    "row"
  );
  const firstPosition = geometry.positionsByNodeId.get(first)!;
  const dx = right + SHARED_TAIL_GAP - firstPosition.x;
  const dy = centerY - byId.get(first)!.height / 2 - firstPosition.y;
  for (const [id, point] of geometry.positionsByNodeId)
    positions.set(id, { x: point.x + dx, y: point.y + dy });
  for (const [id, path] of geometry.pathsByEdgeId)
    paths.set(id, translated(path, dx, dy));
  // Keep the safe corridor outside every row. The renderer restores the smooth
  // horizontal-tangent curve when the space between the endpoints is clear.
  for (const edge of incoming) {
    const source = positions.get(edge.source)!,
      target = positions.get(edge.target)!;
    const sourceBox = byId.get(edge.source)!,
      targetBox = byId.get(edge.target)!;
    const start = {
      x: source.x + sourceBox.width,
      y: source.y + sourceBox.height / 2,
    };
    const end = { x: target.x, y: target.y + targetBox.height / 2 };
    paths.set(
      edge.id,
      pointsToPath([
        start,
        { x: right + SHARED_TAIL_GAP / 2, y: start.y },
        { x: right + SHARED_TAIL_GAP / 2, y: end.y },
        end,
      ])
    );
  }
}
